import { fileToBase64 } from './fileUtils';
import { StepImageDimensions, ZoneConfig, ZoneImageState } from '../types';
import {
  logError,
  loadImage,
  setupCanvas,
  calculateImageScaling,
  renderUserImageWithMask,
  renderUserImageWithoutMask
} from './canvasRenderer';
import { renderAllZones } from './multiZoneRenderer';
import { supportsWebP } from '../../../utilities/webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

/**
 * Handles the save action from the photo editor with PNG masking
 * Creates a pre-masked image that can be sent directly to the Node.js function
 * This eliminates the need for complex masking operations in the backend
 */
export interface SaveHandlerOptions {
  // Single zone mode
  imageUrl: string | null;
  selectedImage: File | null;
  rotation: number;
  scale: number;
  flipX: boolean;
  offsetX: number;
  offsetY: number;

  // Multi-zone mode
  zones?: ZoneConfig[];
  zoneImages?: Map<number, ZoneImageState>;
  isMultiZoneMode?: boolean;

  // Common options
  onFileSelect:
    | ((
        data: { userImage: { base64: string; file: File }; editedImage: { base64: string; file: File } } | null
      ) => void)
    | undefined;
  stepImageDimensions: StepImageDimensions | null;
  uploadType: 'photo' | 'engraving';
  maskSvgBlob: Blob | null;
  maskImageConfig?: { url: string; width: number; height: number };
  setIsProcessing: (value: boolean) => void;
  setIsEditorOpen: (value: boolean) => void;
  setImageUrl: (value: string | null) => void;
  setError: (error: string) => void;
  positioning?: { x: number; y: number; scale: number; rotation: number };
  showStarMapSlot?: boolean;
}

export const createSaveHandler = (options: SaveHandlerOptions): (() => void) => {
  const {
    imageUrl,
    selectedImage,
    rotation,
    scale,
    flipX,
    offsetX,
    offsetY,
    zones = [],
    zoneImages = new Map(),
    isMultiZoneMode = false,
    onFileSelect,
    stepImageDimensions,
    uploadType,
    maskSvgBlob,
    maskImageConfig,
    setIsProcessing,
    setIsEditorOpen,
    setImageUrl,
    setError,
    positioning,
    showStarMapSlot = false
  } = options;

  return async () => {
    setIsProcessing(true);

    try {
      // Setup canvas with standardized dimensions
      const { canvas, ctx, actualWidth, actualHeight } = setupCanvas({ stepImageDimensions });

      if (isMultiZoneMode && zones.length > 0 && zoneImages.size > 0 && maskSvgBlob && maskImageConfig) {
        // Multi-zone mode: render all zones
        await renderAllZones(ctx, {
          zones,
          zoneImages,
          stepImageDimensions,
          uploadType,
          maskSvgBlob,
          maskImageConfig,
          positioning,
          showStarMapSlot,
          actualWidth,
          actualHeight
        });

        // Get the first zone's file for naming (or use a default name)
        const firstZoneImage = Array.from(zoneImages.values())[0];
        const outputFileName = firstZoneImage?.file.name || `composite-image.${imageType.split('/').at(1)}`;

        // Convert canvas to blob
        canvas.toBlob(
          async (blob) => {
            if (blob) {
              try {
                // Convert blob to File object
                const editedFile = new File([blob], outputFileName, { type: imageType });
                const base64 = await fileToBase64(editedFile);

                // Send composite image to onFileSelect
                // For multi-zone, we only send the composite (edited) image
                if (onFileSelect) {
                  // Use the first zone's original file as the "user image" reference
                  const userImageFile = firstZoneImage?.file || new File([], `composite.${imageType}`);
                  const userImageBase64 = firstZoneImage ? await fileToBase64(userImageFile) : base64;

                  onFileSelect({
                    userImage: { base64: userImageBase64, file: userImageFile },
                    editedImage: { base64, file: editedFile }
                  });
                }
                setIsEditorOpen(false);
              } catch (error) {
                logError('Error processing multi-zone save result', error, {
                  hasBlob: !!blob,
                  zoneCount: zones.length
                });
                setError('Failed to process the saved image. Please try again.');
              }
            }
          },
          imageType,
          0.7
        );
      } else if (!isMultiZoneMode && imageUrl && selectedImage) {
        // Single zone mode: existing behavior
        const isCurrentImageStarMap = selectedImage?.name === `star-map.${imageType}` || showStarMapSlot || false;

        // Load the user's uploaded image
        const uploadedImg = await loadImage({ src: imageUrl });

        // Calculate image scaling
        const { scaledWidth, scaledHeight } = calculateImageScaling({
          image: uploadedImg,
          canvasWidth: actualWidth,
          canvasHeight: actualHeight
        });

        // Render with or without mask
        if (maskSvgBlob) {
          await renderUserImageWithMask(ctx, uploadedImg, scaledWidth, scaledHeight, {
            rotation,
            scale,
            flipX,
            offsetX,
            offsetY,
            uploadType,
            showStarMapSlot: isCurrentImageStarMap,
            maskSvgBlob,
            positioning,
            actualWidth,
            actualHeight
          });
        } else {
          renderUserImageWithoutMask(ctx, uploadedImg, scaledWidth, scaledHeight, {
            rotation,
            scale,
            flipX,
            offsetX,
            offsetY,
            uploadType,
            showStarMapSlot: isCurrentImageStarMap
          });
        }

        // Convert canvas to blob
        canvas.toBlob(
          async (blob) => {
            if (blob) {
              try {
                // Convert blob to File object to match FileInput interface
                const filenameWithoutExtension = selectedImage.name.split('.').at(0);
                const editedFile = new File([blob], `${filenameWithoutExtension}.${imageType.split('/').at(1)}`, {
                  type: imageType
                });
                const base64 = await fileToBase64(editedFile);

                // Send original image to onFileSelect
                if (onFileSelect) {
                  const originalBase64 = await fileToBase64(selectedImage);
                  onFileSelect({
                    userImage: { base64: originalBase64, file: selectedImage },
                    editedImage: { base64, file: editedFile }
                  });
                }
                setIsEditorOpen(false);
                if (imageUrl) {
                  URL.revokeObjectURL(imageUrl);
                  setImageUrl(null);
                }
              } catch (error) {
                logError('Error processing save result', error, {
                  hasBlob: !!blob,
                  fileName: selectedImage.name
                });
                setError('Failed to process the saved image. Please try again.');
              }
            }
          },
          imageType,
          0.7
        );
      } else {
        setError('No images to save');
        setIsProcessing(false);
      }
    } catch (error) {
      logError('Error in save handler', error, {
        isMultiZoneMode,
        imageUrl: !!imageUrl,
        hasMask: !!maskSvgBlob,
        uploadType
      });
      setError('Failed to save the image. Please try again.');
      setIsProcessing(false);
    }
  };
};
